import { AfterViewInit, ChangeDetectionStrategy, Component, HostListener, inject, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { LearningTrailsService } from '@app/core';
import { CourseService } from '@app/core/services/course.service';
import { SHARED_MODULES } from '@app/shared';
import { safeEmpty } from '@app/shared/utils';
import { AppSelectors } from '@app/store';
import { AppState } from '@app/store/app.state';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { NgxChartsModule } from '@swimlane/ngx-charts';
import { combineLatest, filter, finalize, Observable, tap } from 'rxjs';

@Component({
  standalone: true,
  imports: [SHARED_MODULES, NgxChartsModule],
  templateUrl: './course-dashboard.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CourseDeshboardComponent implements OnInit, AfterViewInit {
  private readonly _store = inject<Store<AppState>>(Store);
  private readonly _courseService = inject(CourseService);
  private readonly _translateService = inject(TranslateService);
  private readonly _leanringTrailsService = inject(LearningTrailsService);
  public data$: Observable<any>;
  public totalCourses: number;
  public loading = false;

  public view: [number, number] = [500, 400];
  public readonly colorScheme = { domain: ['#199357', '#4adf80', '#61a6fb', '#f97172', '#facc16'] };
  public readonly circumference = 251.3272;
  public chartData: any[] = [];
  public yearsCtrl = new FormControl(undefined);
  public years: { id: number; nome: string }[] = [];
  public legend: boolean = true;

  public ngOnInit(): void {
    this.yearsCtrl.valueChanges.pipe(filter((value) => !!value)).subscribe((value) => this.loadData(value));
    this.yearsCtrl.setValue(new Date().getFullYear());
    this.initForm();
  }

  public ngAfterViewInit(): void {
    this.updateChartSizes();
  }

  @HostListener('window:resize', ['$event'])
  public onResize(event: any) {
    this.updateChartSizes();
  }

  public updateChartSizes() {
    const width = window.innerWidth;

    if (width < 900) {
      this.view = [350, 300];
      this.legend = false;
    } else if (width >= 900 && width < 1200) {
      this.view = [500, 300];
      this.legend = false;
    } else if (width >= 1200 && width < 1550) {
      this.view = [500, 300];
      this.legend = true;
    } else {
      this.view = [600, 300];
      this.legend = true;
    }
  }

  public onStokeDashoffset(progress: number): number {
    return this.circumference - (progress / 100) * this.circumference;
  }

  public onRedeemCertificate(course: any) {
    const token$ = this._store.select(AppSelectors.token);
    const activeUser$ = this._store.select(AppSelectors.ActiveUser);
    combineLatest([token$, activeUser$]).subscribe(([token, activeuser]) => {
      if (token && activeuser) {
        this._courseService.withdrawCertificate(course.evento.id, course.slug, token, activeuser);
      }
    });
  }

  private loadData(value) {
    this.loading = true;
    this.data$ = this._leanringTrailsService.dashboard(value).pipe(
      safeEmpty(),
      tap(({ total }) => (this.totalCourses = total)),
      tap((res) => this.loadGraphicData(res)),
      finalize(() => (this.loading = false)),
    );
  }

  private initForm() {
    const currentYear = new Date().getFullYear();
    for (let i = 0; i <= 2; i++) {
      this.years.push({ id: currentYear - i, nome: (currentYear - i).toString() });
    }
  }

  private loadGraphicData(data: any) {
    this.chartData = [
      { name: `${this._translateService.instant('trilhas.dashboard.total-cursos')}`, value: data.total },
      { name: `${this._translateService.instant('trilhas.dashboard.realizados')}`, value: data.concluidos.total },
      { name: `${this._translateService.instant('trilhas.dashboard.andamento')}`, value: data.em_andamento.total },
      { name: `${this._translateService.instant('trilhas.dashboard.encerrados')}`, value: data.encerrados.total },
      {
        name: `${this._translateService.instant('trilhas.dashboard.nao-concluidos')}`,
        value: data.nao_concluidos.total,
      },
    ];
  }
}
